// @ts-strict-ignore
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';

import {mapValues} from 'common/util';
import {debug} from 'common/report/util';
import {
  CohortsSection,
  FilterSection,
  FormulaSection,
  GroupSection,
  Section,
  ShowSection,
  TimeSection,
} from 'common/report/insights/models/section';
import {InsightsResourceType} from 'common/types/reports/bookmark';

import {ClauseTypes} from './types';

export class BuilderSections {
  declare cohorts: CohortsSection;
  declare filter: FilterSection;
  declare formula: FormulaSection;
  declare group: GroupSection;
  declare show: ShowSection;
  declare time: TimeSection;

  declare globalDataGroupId: string;
  declare metricLevelDataGroups: boolean;

  constructor(
    attrs: {
      show?: ShowSection;
      cohorts?: CohortsSection;
      group?: GroupSection;
      filter?: FilterSection;
      formula?: FormulaSection;
      time?: TimeSection;
      globalDataGroupId?: string;
      metricLevelDataGroups?: boolean;
    } = {},
  ) {
    this.show = attrs.show || new ShowSection();
    this.cohorts = attrs.cohorts || new CohortsSection();
    this.group = attrs.group || new GroupSection();
    this.filter = attrs.filter || new FilterSection();
    this.formula = attrs.formula || new FormulaSection();
    this.time = attrs.time || new TimeSection();

    // Insights API requires a data group ID for each clause in each section,
    // even though it will always be one value in the current UI.
    // Setting this global while applying dataGroupId to each clause will allow us to statefully persist
    // dataGroupId in a clean (new) report.
    this.globalDataGroupId = attrs.globalDataGroupId;

    // query-api ignores the global data group when metricLevelDataGroups is set on the bookmark
    this.metricLevelDataGroups = attrs.metricLevelDataGroups ?? false;
  }

  static deserialize(attrs: BuilderSections) {
    const sections = pick(attrs, ClauseTypes);

    return new BuilderSections({
      ...attrs,
      ...mapValues(sections, (section, sectionType) => Section.deserialize(sectionType, section || [])),
    });
  }

  get attrs() {
    return pick(this, ClauseTypes);
  }

  replaceSection(section) {
    const newAttrs = {
      ...this.attrs,
      [section.TYPE]: section,
      globalDataGroupId: this.globalDataGroupId,
      metricLevelDataGroups: this.metricLevelDataGroups,
    };

    return this.validate(new BuilderSections(newAttrs));
  }

  removeEmptySections() {
    return this.validate(new BuilderSections(pickBy(this.attrs, (section) => section.hasClauses())));
  }

  mapNonEmptySections(mapFunc) {
    // Currently only toUrlData uses this function. The other serialization functions can be updated to this
    // to use this once it's been validated there's no code that depends on empty sections.
    return mapValues(
      pickBy(this.attrs, (section) => section.hasClauses()),
      mapFunc,
    );
  }

  serialize() {
    return {
      show: this.show.serialize(),
      cohorts: this.cohorts.serialize(),
      group: this.group.serialize(),
      filter: this.filter.serialize(),
      formula: this.formula.serialize(),
      time: this.time.serialize(),
      ...(this.globalDataGroupId ? {globalDataGroupId: this.globalDataGroupId} : null),
      ...(this.metricLevelDataGroups ? {metricLevelDataGroups: this.metricLevelDataGroups} : null),
    };
  }

  toBookmarkData() {
    const output = {
      ...mapValues(this.attrs, (section) => section.toBookmarkData()),
      ...(this.globalDataGroupId ? {globalDataGroupId: this.globalDataGroupId} : null),
      ...(this.metricLevelDataGroups ? {metricLevelDataGroups: this.metricLevelDataGroups} : null),
    };
    return output;
  }

  toUrlData() {
    return {
      ...this.mapNonEmptySections((section) => section.toUrlData()),
      ...(this.globalDataGroupId ? {globalDataGroupId: this.globalDataGroupId} : null),
      ...(this.metricLevelDataGroups ? {metricLevelDataGroups: this.metricLevelDataGroups} : null),
    };
  }

  get valid() {
    return (
      this.show instanceof ShowSection &&
      this.show.valid &&
      this.cohorts instanceof CohortsSection &&
      this.cohorts.valid &&
      this.group instanceof GroupSection &&
      this.group.valid &&
      this.filter instanceof FilterSection &&
      this.filter.valid &&
      this.formula instanceof FormulaSection &&
      this.formula.valid &&
      this.time instanceof TimeSection &&
      this.time.valid
    );
  }

  setDataGroupId({dataGroupId, customEvents, metricsResource, behaviorsResource}) {
    this.globalDataGroupId = dataGroupId;

    return this.replaceSection(
      this.show
        .setDataGroupId(dataGroupId)
        .makeValidForDataGroup({dataGroupId, customEvents, metricsResource, behaviorsResource}),
    )
      .replaceSection(
        this.group
          .setDataGroupIdByResourceType(dataGroupId, InsightsResourceType.Events)
          .removeUserClauses(dataGroupId),
      )
      .replaceSection(this.cohorts.makeValidForDataGroup(dataGroupId))
      .replaceSection(
        this.filter
          .setDataGroupIdByResourceType(dataGroupId, InsightsResourceType.Events)
          .makeValidForDataGroup(dataGroupId),
      );
  }

  getDataGroupId() {
    if (this.metricLevelDataGroups) {
      return ``;
    }
    const behaviorClauses = this.show.getBehaviorClauses();
    const clauseGroupId = behaviorClauses.length ? behaviorClauses[0].behavior.dataGroupId : null;
    return this.globalDataGroupId ?? clauseGroupId;
  }

  validate(newSections) {
    const valid = newSections.valid;

    if (!valid) {
      debug.warn(`sections invalid:`, newSections);
    }

    return valid ? newSections : this;
  }
}
